import firebase from "firebase/app";
import UserModel, {
  FETCH_USERS_LIMIT,
  RawMyRoutineUser,
} from "@/models/my-routine-users";
import moment from "moment";
import cloneDeep from "lodash/cloneDeep";

export interface MyRoutineUser {
  id: string;
  name?: string;
  email: string;
  phone: string;
  selections: string[];
  createdAt: string;
  updatedAt: string;
  problem?: string;
  solvedBy?: string;
  isSolved?: boolean;
}

export interface MyRoutineUserForDownloading {
  name: string;
  email: string;
  phone: string;
  createdAt: string;
  updatedAt: string;
  problem: string;
  solvedBy: string;
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  step5: string;
}

export default class MyRoutineUserController {
  private lastDocOfNext: firebase.firestore.QueryDocumentSnapshot | null = null;
  private lastDocOfPrevious: firebase.firestore.QueryDocumentSnapshot | null = null;
  private _hasPrevious = false;
  private _hasNext = false;

  get hasPrevious(): boolean {
    return this._hasPrevious;
  }

  get hasNext(): boolean {
    return this._hasNext;
  }

  public async fetchUsersFirstTime(
    isSolved: boolean
  ): Promise<MyRoutineUser[]> {
    const {
      users,
      lastDocOfNext,
      lastDocOfPrevious,
    } = await UserModel.fetchNextUsers(isSolved);
    this.lastDocOfPrevious = lastDocOfPrevious;
    this.lastDocOfNext = lastDocOfNext;

    this._hasNext = users.length == FETCH_USERS_LIMIT;

    return this.processRawUsers(users);
  }

  private processRawUsers(rawUsers: RawMyRoutineUser[]): MyRoutineUser[] {
    return rawUsers.map(rawUser => {
      const user: any = cloneDeep(rawUser);
      user.createdAt = this.convertToDisplayDate(user.createdAt);
      user.updatedAt = this.convertToDisplayDate(user.updatedAt);
      return user;
    });
  }

  public async fetchNextUsers(isSolved: boolean): Promise<MyRoutineUser[]> {
    const {
      users,
      lastDocOfPrevious,
      lastDocOfNext,
    } = await UserModel.fetchNextUsers(
      isSolved,
      this.lastDocOfNext as firebase.firestore.QueryDocumentSnapshot
    );
    this.lastDocOfPrevious = lastDocOfPrevious;
    this.lastDocOfNext = lastDocOfNext;

    this._hasPrevious = true;
    this._hasNext = users.length == FETCH_USERS_LIMIT;

    return this.processRawUsers(users);
  }

  public async fetchPreviousUsers(isSolved: boolean): Promise<MyRoutineUser[]> {
    const {
      users,
      lastDocOfPrevious,
      lastDocOfNext,
    } = await UserModel.fetchPreviousUsers(
      isSolved,
      this.lastDocOfPrevious as firebase.firestore.QueryDocumentSnapshot
    );
    this.lastDocOfPrevious = lastDocOfPrevious;
    this.lastDocOfNext = lastDocOfNext;

    this._hasNext = true;
    this._hasPrevious = users.length == FETCH_USERS_LIMIT;

    return this.processRawUsers(users);
  }

  public async searchUsersByPhone(
    phone: string,
    isSolved: boolean
  ): Promise<MyRoutineUser[]> {
    const rawUsers = await UserModel.searchUsersByPhone(phone, isSolved);
    return this.processRawUsers(rawUsers);
  }

  private convertToDisplayDate(dateMsec: number): string {
    const date = new Date(dateMsec);
    return moment(date).format("YYYY-MM-DD");
  }

  public async updateMyRoutineUsersSolvedBy(
    docId: string,
    solverEmail: string
  ) {
    return UserModel.updateMyRoutineUserSolvedBy(docId, solverEmail);
  }

  public async deleteUser(docId: string) {
    return UserModel.deleteUser(docId);
  }

  public async getDownloadData(
    isSolved: boolean,
    startDate: Date,
    endDate: Date
  ): Promise<MyRoutineUserForDownloading[]> {
    const rawUsers = await UserModel.fetchUsersForDownloading(
      isSolved,
      startDate.getTime(),
      endDate.getTime()
    );
    return this.processRawDownloadData(rawUsers);
  }

  private processRawDownloadData(
    users: RawMyRoutineUser[]
  ): MyRoutineUserForDownloading[] {
    const csvData: MyRoutineUserForDownloading[] = [];

    users.forEach(user => {
      const selections = user.selections;

      if (!selections || selections.length < 5) {
        return;
      }

      const csvRow: MyRoutineUserForDownloading = {
        name: user.name || "",
        email: user.email,
        phone: user.phone || "",
        createdAt: this.convertToDownloadDate(user.createdAt),
        updatedAt: this.convertToDownloadDate(user.updatedAt),
        problem: user.problem || "",
        solvedBy: user.solvedBy || "",
        step1: selections[0],
        step2: selections[1],
        step3: selections[2],
        step4: selections[3],
        step5: selections[4],
      };

      csvData.push(csvRow);
    });

    return csvData;
  }

  private convertToDownloadDate(dateMsec: number): string {
    const date = new Date(dateMsec);
    return moment(date).format("YYYY-MM-DD hh:mm:ss");
  }
}
